<template>
  <div>
    <PageHeader :title="title" :subtitle="subtitle" :breadcrumbs="DETAIL_CRUMBS">
      <template #title-suffix>
        <button type="button" class="order-first inline-flex items-center text-gray-600 hover:text-gray-900" aria-label="Back" @click="goBack($router)">
          <font-awesome-icon :icon="['fas', 'arrow-left']" class="w-3.5 h-3.5" />
        </button>
      </template>
      <template v-if="study" #actions>
        <router-link :to="{ path: '/agent', query: { study: study.id } }" class="btn-secondary-sm">Open in agent</router-link>
        <button type="button" class="btn-primary-sm" :disabled="starting || !study.script" @click="runStudy">{{ starting ? 'Starting…' : 'Run' }}</button>
      </template>
      <p v-if="study && study.description" class="text-gray-700" style="font-size: 13px;">{{ study.description }}</p>
      <template v-if="study" #bottom>
        <nav class="flex gap-6 px-6 -mb-px" aria-label="Study sections">
          <button
            v-for="t in TABS"
            :key="t.key"
            type="button"
            :class="['py-3 text-[12px] font-medium border-b-2', t.key === tab ? 'border-primary-500 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-900']"
            @click="setQuery({ tab: t.key === 'results' ? undefined : t.key, edit: undefined })"
          >
            {{ t.label }}
          </button>
        </nav>
      </template>
    </PageHeader>

    <div class="p-6 pb-32 text-[11px]">
      <LoadingIndicator v-if="studyId && !study && !error" message="Loading study..." />
      <div v-else-if="error" class="rounded-md bg-red-tint px-4 py-3 text-gray-900">{{ error }}</div>

      <div v-else-if="!studyId" class="bg-white rounded-lg border border-gray-200 p-4">
        <StudyEditor :attributes="catalog.attributes" :datasets="catalog.datasets" :frames="catalog.frames" @saved="onSaved" />
      </div>

      <template v-else>
        <div v-if="runError" class="rounded-md bg-red-tint px-4 py-3 mb-4 text-gray-900">{{ runError }}</div>

        <section v-if="tab === 'results'" class="flex gap-4 items-start">
          <div v-if="!runs.length" class="bg-white rounded-lg border border-gray-200 p-4 flex-1 text-gray-500">This study has not been run.</div>
          <template v-else>
            <aside class="w-72 shrink-0 bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div class="px-3 py-2 border-b border-gray-200 flex items-center gap-2">
                <span class="text-gray-500">{{ runs.length }} run{{ runs.length === 1 ? '' : 's' }}</span>
                <button v-if="picked.length === 2" type="button" class="btn-secondary-sm ml-auto" @click="setQuery({ compare: picked.join(',') })">Compare</button>
                <span v-else class="ml-auto text-gray-400">Tick two to compare</span>
              </div>
              <div class="divide-y divide-gray-100 max-h-[calc(100vh-18rem)] overflow-y-auto">
                <div
                  v-for="r in runs"
                  :key="r.id"
                  :class="['flex items-start gap-2 px-3 py-2 cursor-pointer', !compareIds.length && r.id === selectedId ? 'bg-primary-50' : 'hover:bg-gray-50']"
                  @click="setQuery({ run: r.id === latestId ? undefined : r.id, compare: undefined })"
                >
                  <input v-model="picked" type="checkbox" class="mt-0.5" :value="r.id" :disabled="picked.length === 2 && !picked.includes(r.id)" :aria-label="`Compare run ${r.id}`" @click.stop>
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-2">
                      <span class="text-gray-900 font-medium tabular-nums">Run {{ r.id }}</span>
                      <HermesBadge :value="r.status" />
                    </div>
                    <div class="text-gray-500" :title="absolute(r.run_at)">{{ relative(r.run_at) }}<template v-if="r.run_by_name"> · <span class="sensitive" data-spoof-kind="person">{{ r.run_by_name }}</span></template></div>
                    <div class="sensitive text-gray-500 truncate">{{ r.changes.join(' · ') }}</div>
                  </div>
                </div>
              </div>
            </aside>

            <div v-if="compareIds.length === 2" class="flex-1 min-w-0 bg-white rounded-lg border border-gray-200">
              <div class="px-4 py-3 border-b border-gray-200 flex items-center">
                <span class="text-[12px] font-semibold text-gray-900">Runs {{ compareIds[0] }} and {{ compareIds[1] }}</span>
                <button type="button" class="ml-auto text-neutral-800" @click="setQuery({ compare: undefined })">Close comparison</button>
              </div>
              <div class="p-4 grid grid-cols-2 gap-6">
                <StudyRunReport v-for="id in compareIds" :key="id" :run-id="id" class="min-w-0" />
              </div>
            </div>
            <div v-else class="flex-1 min-w-0 bg-white rounded-lg border border-gray-200 p-4">
              <StudyRunReport :key="selectedId" :run-id="selectedId" @settled="refreshRuns" />
            </div>
          </template>
        </section>

        <section v-else-if="tab === 'inputs'" class="grid grid-cols-3 gap-3">
          <div v-if="!inputs.length" class="text-gray-500">The script declares no inputs.</div>
          <div v-for="i in inputs" :key="i.key" class="bg-white rounded-lg border border-gray-200 p-3 min-w-0">
            <div class="text-gray-500 uppercase tracking-wider">{{ i.kind }}</div>
            <router-link v-if="i.frameId" :to="`/insight/impact-analytics/frames/${i.frameId}`" class="sensitive block text-neutral-800 font-medium break-words">{{ i.name }}</router-link>
            <div v-else class="sensitive text-gray-900 font-medium break-words">{{ i.name }}</div>
            <div v-if="i.rows != null" class="text-gray-500">{{ i.rows.toLocaleString() }} rows read by run {{ selectedId }}</div>
          </div>
        </section>

        <section v-else-if="tab === 'script'" class="bg-white rounded-lg border border-gray-200 p-4">
          <StudyEditor v-if="editing" :study-id="studyId" :attributes="catalog.attributes" :datasets="catalog.datasets" :frames="catalog.frames" @saved="onSaved" @ran="onRan" />
          <template v-else>
            <div class="flex items-center mb-3">
              <span class="text-gray-500">{{ study.template_key ? `From template ${study.template_key}` : 'R script' }}</span>
              <button type="button" class="btn-secondary-sm ml-auto" @click="setQuery({ edit: '1' })">Edit script</button>
            </div>
            <pre class="px-4 py-3 rounded bg-gray-900 text-gray-100 overflow-x-auto">{{ study.script || '# No script yet.' }}</pre>
          </template>
        </section>

      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import HermesBadge from '@/components/Admin/Hermes/HermesBadge.vue';
import { absolute, relative } from '@/components/Admin/Hermes/hermes';
import StudyRunReport from './StudyRunReport.vue';
import StudyEditor from './StudyEditor.vue';
import { DETAIL_CRUMBS, goBack } from './impactNav';

const SOURCES = { builtin: 'Built-in study', saved: 'Saved study', agent: 'Agent working study' };
const KINDS = { saved: 'Saved frame', attribute: 'DataAttribute', dataset: 'Dataset' };
const TABS = [
  { key: 'results', label: 'Results' },
  { key: 'inputs', label: 'Inputs' },
  { key: 'script', label: 'Script' },
];

export default {
  name: 'StudyPage',
  components: { PageHeader, LoadingIndicator, HermesBadge, StudyRunReport, StudyEditor },
  props: {
    studyId: { type: Number, default: null },
  },
  data() {
    return {
      study: null, runs: [], error: '', runError: '', starting: false, picked: [],
      catalog: { attributes: [], datasets: [], frames: [] }, catalogLoaded: false,
      TABS, DETAIL_CRUMBS,
    };
  },
  computed: {
    title() {
      if (!this.studyId) return 'New study';
      return this.study ? this.study.name : ' ';
    },
    subtitle() {
      if (!this.study) return '';
      return [SOURCES[this.study.origin], this.study.template_key, this.study.created_by_name].filter(Boolean).join(' · ');
    },
    tab() {
      const wanted = this.$route.query.tab;
      return TABS.some((t) => t.key === wanted) ? wanted : 'results';
    },
    editing() {
      return !this.studyId || this.$route.query.edit === '1';
    },
    runById() {
      return Object.fromEntries(this.runs.map((r) => [r.id, r]));
    },
    latestId() {
      return this.runs.length ? this.runs[0].id : null;
    },
    selectedId() {
      const wanted = Number(this.$route.query.run);
      return this.runById[wanted] ? wanted : this.latestId;
    },
    compareIds() {
      const ids = String(this.$route.query.compare || '').split(',').map(Number).filter((id) => this.runById[id]);
      return ids.length === 2 ? ids.sort((a, b) => a - b) : [];
    },
    inputs() {
      const run = this.runById[this.selectedId];
      const read = Object.fromEntries(((run && run.inputs) || []).map((i) => [i.role, i]));
      return ((this.study && this.study.inputs) || []).map((spec) => {
        const frame = read[spec.slug];
        return {
          key: `${spec.source_type}:${spec.slug}`,
          kind: KINDS[spec.source_type] || spec.source_type,
          name: frame ? frame.name : spec.slug,
          frameId: frame ? frame.frame_id : null,
          rows: frame ? frame.row_count : null,
        };
      });
    },
  },
  watch: {
    studyId() {
      this.load();
    },
    editing: {
      immediate: true,
      handler(value) {
        if (value) this.loadCatalog();
      },
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    absolute,
    relative,
    goBack,
    async load() {
      this.error = '';
      this.study = null;
      this.runs = [];
      if (!this.studyId) return;
      try {
        const [study, runs] = await Promise.all([
          axios.get(`api/impact_analytics/studies/${this.studyId}/`),
          axios.get(`api/impact_analytics/studies/${this.studyId}/runs/`),
        ]);
        this.study = study.data;
        this.runs = runs.data;
      } catch (err) {
        this.error = err?.response?.status === 404 ? 'No study with that id on this tenant.' : 'The study could not be loaded.';
      }
    },
    async refreshRuns() {
      const { data } = await axios.get(`api/impact_analytics/studies/${this.studyId}/runs/`);
      this.runs = data;
    },
    async loadCatalog() {
      if (this.catalogLoaded) return;
      this.catalogLoaded = true;
      const [attributes, datasets, frames] = await Promise.all([
        axios.get('api/impact_analytics/attributes/'),
        axios.get('api/etl_module/data-workflows/'),
        axios.get('api/impact_analytics/frames/'),
      ]);
      this.catalog = { attributes: attributes.data, datasets: datasets.data, frames: frames.data };
    },
    setQuery(patch) {
      const query = { ...this.$route.query, ...patch };
      Object.keys(query).forEach((k) => query[k] === undefined && delete query[k]);
      this.$router.replace({ query });
    },
    onSaved(study) {
      if (!this.studyId) {
        this.$router.replace({ path: `/insight/impact-analytics/studies/${study.id}`, query: { tab: 'script', edit: '1' } });
        return;
      }
      this.study = { ...this.study, ...study };
    },
    async onRan() {
      await this.refreshRuns();
      this.setQuery({ tab: undefined, edit: undefined, run: undefined, compare: undefined });
    },
    async runStudy() {
      this.starting = true;
      this.runError = '';
      try {
        await axios.post(`api/impact_analytics/studies/${this.studyId}/run/`, {});
        await this.refreshRuns();
        this.setQuery({ tab: undefined, edit: undefined, run: undefined, compare: undefined });
      } catch (err) {
        this.runError = err?.response?.data?.error || 'The run could not be started.';
      } finally {
        this.starting = false;
      }
    },
  },
};
</script>
